/**
 * Identity route factory. Pages stay router-free and session-free; this file
 * reads the session, gates every control on the exact permission the generated
 * policy demands, and supplies the branch list the user editor assigns from.
 */
import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { apiFetch, type Page } from "@/lib/api/http";
import { useSession } from "@/lib/session/use-session";

import { RolesPage } from "./pages/roles-page";
import { UsersPage, type Branch } from "./pages/users-page";
import { IDENTITY_PERMISSIONS as P, IdentityPermissionBoundary } from "./permissions";

function useAssignableBranches(enabled: boolean) {
  return useQuery<Branch[]>({
    queryKey: ["identity/branches"],
    enabled,
    queryFn: async ({ signal }) => {
      const page = await apiFetch<Page<Branch>>("/branches", { query: { limit: 200 }, signal });
      return page.items;
    },
  });
}

function UsersRoute() {
  const { hasPermission } = useSession();
  // Branch names come from the branch surface under its own permission; a
  // caller without it still administers users, just without names to assign.
  const branches = useAssignableBranches(hasPermission("platform.branch.read"));
  return (
    <IdentityPermissionBoundary permission={P.userRead}>
      <UsersPage
        branches={branches.data ?? []}
        canCreate={hasPermission(P.userWrite)}
        canUpdate={hasPermission(P.userWrite)}
        canSetPassword={hasPermission(P.userPassword)}
      />
    </IdentityPermissionBoundary>
  );
}

function RolesRoute() {
  const { hasPermission } = useSession();
  return (
    <IdentityPermissionBoundary permission={P.roleRead}>
      <RolesPage canCreate={hasPermission(P.roleWrite)} canUpdate={hasPermission(P.roleWrite)} />
    </IdentityPermissionBoundary>
  );
}

// The return type is erased to AnyRoute[] deliberately: the route tree's own
// literal types are what give `to="/login"` its typed search params, and a
// factory that leaks concrete route types into addChildren collapses them for
// the whole tree.
export function identityRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({ getParentRoute: () => parent, path: "admin/users", component: UsersRoute }),
    createRoute({ getParentRoute: () => parent, path: "admin/roles", component: RolesRoute }),
  ] as AnyRoute[];
}
